<script>
  import ModalButtons from 'root/services/modals/ModalButtons.YesNo.svelte';
  import ClosableModal from 'root/services/modals/Closable.modal.svelte';
  import {rfpManagerRepository, httpErrorHandler, notificationUtils, mainAPI} from 'root/angular-injector-provider';
  import orderBy from 'lodash/orderBy';
  import SpinnerWithText from 'root/components/molecules/SpinnerWithText.svelte';
  import UserDetailsHeader from 'vRoot/account-profile/modules/manage-users/UserDetailsHeader.svelte';
  import {comparisonForStrings} from 'root/services/tools';
  import getSelectionIcon from 'root/services/selection-icons.service';

  export let cancel, hide, userAccount;

  const LOADING = 'LOADING';

  let
    preselectedAccountsIds = [],
    companies = LOADING,
    selected = [],
    userType,
    working = true,
    noAssignmentChanges = true,
    noUserTypeChanges = true,
    notify = true;

  $: noUserTypeChanges = userType === userAccount.type;

  (function initialize(){
    userType = userAccount.type;
    preselectedAccountsIds = (userAccount.managedAccounts || []);

    rfpManagerRepository().listAllCompanies()
      .then(response => {
        const accounts = response.data
          .filter(a => a.type === 'COMPANY')
          .map(a => {
            a.managedByUser = preselectedAccountsIds.includes(a.id);
            return a;
          });

        selected = [...preselectedAccountsIds];
        noAssignmentChanges = true;

        companies = orderBy(accounts, ['managedByUser', 'name'], ['desc', 'asc']);
        working = false;
      })
      .catch(error => httpErrorHandler().handle(error));
  })();

  function toggleSelected(companyId){
    selected = selected.includes(companyId)? selected.filter(s => s !== companyId): [...selected, companyId];
    noAssignmentChanges = selected.length === preselectedAccountsIds.length && !selected.filter(s => !preselectedAccountsIds.includes(s)).length;
  }

  function save(){
    if(noAssignmentChanges && noUserTypeChanges) hide();
    working = true;
    const type = userType === 'Administrator' ? 'ADMIN' : 'USER';
    const updateFunction = userAccount.status === 'INVITED'?
      mainAPI().manageAccount.updateInviteeAssignmentsAndType :
      mainAPI().manageAccount.updateUserAssignmentsAndType;

    notificationUtils().onSave(() => updateFunction(
      userAccount.accountId,
      userAccount._id,
      type,
      selected,
      notify
    ))
      .then(hide);
  }

</script>


<ClosableModal width="650px" close="{cancel}">
  <div slot="heading">
    <UserDetailsHeader
      isEditable="{true}"
      {userAccount}
      {userType}
      onChange="{type => {userType = type}}"
    />
  </div>

  {#if companies === LOADING}
    <div class="Notification">
      <SpinnerWithText />
    </div>
  {:else if !companies.length}
    <div class="Notification">
      There are no companies to assign!
    </div>
  {:else}
    <div class="Body">
      <div class="CompaniesHeader">
        <span>
          {#if selected.length}
            User is assigned to { selected.length } Compan{ selected.length > 1? 'ies': 'y' }
          {:else}
            User is not assigned to any company
          {/if}
        </span>
        <span>Managers:</span>
      </div>


      <div class="Companies">
        {#each companies as company}
          <div
            class="Company"
            class:ManagedByUser={company.managedByUser}
            data-icon-before="{selected.includes(company.id)? 'check_box': 'check_box_outline_blank'}"
            on:click={() => toggleSelected(company.id)}
          >
            <div class="CompanyName">{company.name}</div>

            <div class="CompanyAgent">
              {#if company.managers && company.managers.length}
                <div>
                  {company.managers.length} Manager{company.managers.length === 1? '': 's'}
                  <div class="rbTooltip simple ManagersTooltip" tooltip="placement:top-end">
                    <div>Account Managers:</div>
                    <div>{company.managers.map(m => m.userDetails.fullName).sort(comparisonForStrings).join(', ')}</div>
                  </div>
                </div>
              {:else}
                <span class="NotAssigned" data-icon-after="warning">Not Assigned</span>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    </div>


    <div class="NotifyCheckbox"
         class:On="{notify}"
         data-icon-before="{getSelectionIcon(notify)}"
         on:click={() => notify = !notify}
    >
      Notify Managers of changes.
    </div>
  {/if}

  <ModalButtons
    disabled="{working || (noUserTypeChanges && noAssignmentChanges)}"
    noAction="{cancel}"
    noLabel="Cancel"
    yesLabel="Apply Changes"
    yesIcon="account_circle"
    yesAction="{save}"
  />
</ClosableModal>

<style lang="stylus">

  .Notification {
    padding: 20px;
    font-size: 14px;
    font-weight: bold;
    color #546E7A;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .Body {
    margin-top: 15px;
    margin-bottom: 15px;
    padding: 0 15px;
    color #546E7A;
    font-size 13px;
  }

  .CompaniesHeader {
    display flex;
    justify-content space-between;
    align-items center;
    padding 5px;
  }

  .Companies {
    overflow: auto;
    max-height: 50vh;
    border: 1px solid #B0BEC5;
    border-radius: 5px;
  }

  .Company {
    padding: 10px;
    height: 42px;
    border-bottom: 1px solid #eceff1;
    display: flex;
    align-items: center;
    cursor: pointer;
    box-sizing: border-box;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  :nth-last-child(1 of .ManagedByUser) {
    border-bottom 3px solid #CFD8DC;
  }

  .CompanyName {
    margin-left: 5px;
    white-space: nowrap;
  }

  .CompanyAgent {
    flex: 1 1 100%;
    white-space: nowrap;
    text-align: right;
  }

  .NotAssigned {
    color: #f25b26;

    &:after {
      font-size: 16px;
      margin-left: 3px;
    }
  }

  .NotifyCheckbox {
    margin: 0 15px;
    color: #546e7a;
    font-size: 12px;
    gap: 5px;
    cursor: pointer;
    width: fit-content;


    &:before {
      font-size: 18px;
      height: 18px;
    }

    &.On {
      color: #00A99D;
    }

    &:hover {
      color: #37474f;
    }
  }

  .ManagersTooltip {
    max-width: 400px;
    text-align: left;

    div:first-child {
      text-align: center;
      font-weight: bold;
    }
  }
</style>
